import React, {
    useState,
    useEffect,
    useRef,
    useContext,
    useMemo,
    useCallback,
} from "react";
import socket from "../../services/socket-io";
import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";
import { toast } from "react-toastify";
import { sanitizeTicket, sanitizeContact } from "../../helpers/sanitizeContact";
import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";
import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import IgnoreModalContext from "../../context/IgnoreModal/IgnoreModalContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import { hashDiagnosticId, recordDiagnosticEvent, useDiagnosticRender } from "../../services/frontendDiagnostics";

const useStyles = makeStyles((theme) => ({
    ticketsListWrapper: {
        position: "relative",
        display: "flex",
        height: "100%",
        flexDirection: "column",
        overflow: "hidden",
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
    },
    ticketsList: {
        flex: 1,
        overflowY: "scroll",
        ...theme.scrollbarStyles,
        borderTop: "2px solid rgba(0, 0, 0, 0.12)",
    },
    noTicketsDiv: {
        display: "flex",
        height: "100px",
        margin: 40,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
    noTicketsTitle: {
        textAlign: "center",
        fontSize: "16px",
        fontWeight: 600,
        margin: 0,
    },
    noTicketsText: {
        textAlign: "center",
        color: "rgb(104, 121, 146)",
        fontSize: 14,
    },
}));

const TicketsList = ({
    listStatus,
    searchParam,
    showAll,
    showFavoriteTickets,
    selectedQueueIds,
    selectedTags,
    updateCount,
    style,
    selectedTicketId,
    showOnlyIgnored,
}) => {
    const classes = useStyles();
    const [pageNumber, setPageNumber] = useState(1);
    const { ticketsList, dispatch } = useContext(TicketsContext);
    const { user } = useContext(AuthContext);
    const searchRef = useRef(searchParam);
    const tagsRef = useRef(selectedTags || []);
    const ticketsListRef = useRef(ticketsList);
    const updateCountRef = useRef(null);

    const { handleOpenIgnoreModal } = useContext(IgnoreModalContext);

    const tagIdsString = selectedTags?.map(t => t.id).join(',') || '';

    const { tickets, hasMore, loading } = useTickets({
        pageNumber,
        searchParam,
        status: listStatus,
        showAll,
        isFavorite: showFavoriteTickets,
        queueIds: JSON.stringify(selectedQueueIds),
        tagIds: tagIdsString,
        isIgnored: showOnlyIgnored,
    });
    useDiagnosticRender("TicketsList", () => ({
        ticketIdHash: hashDiagnosticId(selectedTicketId),
        counts: {
            ticketsInContext: ticketsList.length,
            fetchedTickets: tickets?.length || 0,
            selectedQueues: selectedQueueIds?.length || 0,
            selectedTags: selectedTags?.length || 0,
            loading: loading ? 1 : 0,
            hasMore: hasMore ? 1 : 0
        },
        metadata: {
            listStatus: listStatus || "all",
            searchActive: searchParam ? "1" : "0"
        }
    }));

    useEffect(() => {
        searchRef.current = searchParam;
    }, [searchParam]);
    useEffect(() => {
        tagsRef.current = selectedTags || [];
    }, [selectedTags]);
    useEffect(() => {
        ticketsListRef.current = ticketsList;
    }, [ticketsList]);

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [
        listStatus,
        searchParam,
        showAll,
        selectedQueueIds,
        showFavoriteTickets,
        showOnlyIgnored,
        tagIdsString,
    ]);

    useEffect(() => {
        if (!tickets || tickets.length === 0) return;

        dispatch({
            type: "LOAD_TICKETS",
            payload: tickets,
            meta: { listStatus, user, pageNumber }
        });
    }, [tickets, pageNumber, dispatch, listStatus, user]);

    useEffect(() => {
        const shouldUpdateTicket = (ticket) => {
          const isSharedWithMe = ticket.sharedWithUsers?.some(sharedUser => sharedUser.id === user?.id);
          const isAssigned = !!ticket.userId;
          const isMine = ticket.userId === user?.id;
          const isShowAll = showAll === 'true' || showAll === true;
          const hasQueue = !!ticket.queueId;
          const isMyQueue = selectedQueueIds.includes(ticket.queueId);
          const isDoctor = user.profile === "doctor";

          const activeTagIds = tagsRef.current?.map(t => t.id) || [];
          const contactTags = ticket.contact?.tags || [];
          const matchesTags = activeTagIds.length === 0 || contactTags.some(tag => activeTagIds.includes(tag.id));

          if (!matchesTags) return false;

          if (ticket.status === "pending" && isDoctor) return false;

          if (isMine || isSharedWithMe || isShowAll) return true;
          if (!isAssigned) return true;
          if (hasQueue && isMyQueue) return true;

          return false;
        };

        const handleConnect = () => {
            if (listStatus) {
                socket.emit("joinTickets", listStatus);
            } else {
                socket.emit("joinNotification");
            }
        };

        const handleTicket = (data) => {
            const dticket = data.ticket;
            if (!dticket) return;

            if (searchRef.current) return;

            if (tagsRef.current && tagsRef.current.length > 0) {
              const activeTagIds = tagsRef.current.map(t => t.id);
              const contactTags = dticket.contact?.tags || [];

              const matches = contactTags.some(tag => activeTagIds.includes(tag.id));

              if (!matches) {
                return;
              }
            }

            if (data.action === "updateFavoriteStatus") {
                const existsInList = ticketsListRef.current.some(t => t.id === dticket.id);

                if (showFavoriteTickets && !dticket.isFavorite && existsInList) {
                    dispatch({
                        type: "DELETE_TICKET",
                        payload: dticket.id,
                        meta: { selectedTicketId, user },
                    });
                    return;
                }

                dispatch({
                    type: "UPDATE_TICKET_FAVORITE",
                    payload: dticket,
                    meta: { showFavoriteTickets, user },
                });
                return;
            }

            if (data.action === "updateUnread") {
                dispatch({
                    type: "UPDATE_TICKET_UNREAD_MESSAGES",
                    payload: data,
                    meta: { listStatus, selectedTicketId, user },
                });
                return;
            }

            if (data.action === "delete" || data.action === "ignore") {
                    const existsInList = ticketsListRef.current.some(t => t.id === data.ticket.id);
                if (!existsInList) return;
                dispatch({
                    type: "DELETE_TICKET",
                    payload: data.ticket.id,
                    meta: { selectedTicketId, user },
                });
                return;
            }

            if (data.action === "update") {
                const newStatus = data.listStatus;
                const oldStatus = data.oldStatus;

                if (listStatus === newStatus || !listStatus || listStatus === "all") {
                    if (!shouldUpdateTicket(dticket)) {
                        return;
                    }
                    dispatch({
                        type: "UPDATE_TICKET",
                        payload: dticket,
                        meta: { listStatus: newStatus, selectedTicketId, user },
                    });
                    return;
                }

                if (listStatus === oldStatus) {
                    const existsInList = ticketsListRef.current.some(t => t.id === dticket.id);
                    if (existsInList) {
                        if (!shouldUpdateTicket(dticket)) {
                            return;
                        }
                        dispatch({
                            type: "DELETE_TICKET",
                            payload: dticket.id,
                            meta: { selectedTicketId, user },
                        });
                    }
                    return;
                }

                return;
            }
        };

        const handleAppMessage = (data) => {
            if (searchRef.current || (tagsRef.current && tagsRef.current.length > 0)) return;
            
            const ticket = data.ticket || (data.message && data.message.ticket);
            if (!ticket) return;

            if (showFavoriteTickets) {
                const isAlreadyInList = ticketsListRef.current.some(t => t.id === ticket.id);
                
                if ((isAlreadyInList || ticket.isFavorite) && shouldUpdateTicket(ticket)) {
                    dispatch({
                        type: "UPDATE_TICKET_ON_MESSAGE",
                        payload: ticket,
                        meta: { selectedTicketId, user },
                    });
                }
                return;
            }

            if (data.action === "create" && shouldUpdateTicket(ticket)) {
                dispatch({
                    type: "UPDATE_TICKET_ON_MESSAGE",
                    payload: ticket,
                    meta: { selectedTicketId, user },
                });
            }
        };

        socket.on("connect", handleConnect);
        socket.on("ticket", handleTicket);
        socket.on("appMessage", handleAppMessage);

        if (socket.connected) {
            handleConnect();
        }

        return () => {
            socket.off("connect", handleConnect);
            socket.off("ticket", handleTicket);
            socket.off("appMessage", handleAppMessage);
        };
    }, [
        listStatus,
        searchParam,
        showAll,
        user,
        selectedQueueIds,
        showFavoriteTickets,
        selectedTicketId
    ]);

    const loadMore = useCallback(() => setPageNumber((prev) => prev + 1), []);

    const handleScroll = useCallback((e) => {
        if (!hasMore || loading) return;
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            e.currentTarget.scrollTop = scrollTop - 100;
            loadMore();
        }
    }, [hasMore, loading, loadMore]);

    const filteredTicketsToDisplay = useMemo(() => ticketsList.filter(ticket => {
        const isDoctor = user.profile === "doctor";
        
        if (ticket.status === "pending" && isDoctor) return false;

        return !listStatus || ticket.status === listStatus;
    }), [ticketsList, listStatus, user.profile]);

    useEffect(() => {
        if (!updateCount || updateCountRef.current === filteredTicketsToDisplay.length) return;
        updateCountRef.current = filteredTicketsToDisplay.length;
        updateCount(filteredTicketsToDisplay.length);
    }, [filteredTicketsToDisplay.length, updateCount]);

    const handleTicketSelect = useCallback((ticketId) => {
        dispatch({
            type: "RESET_UNREAD_MESSAGES",
            payload: { ticketId },
            meta: { selectedTicketId, user },
        });
    }, [dispatch, selectedTicketId, user]);

    useEffect(() => {
        recordDiagnosticEvent({
            eventType: "tickets_list_state",
            component: "TicketsList",
            ticketIdHash: hashDiagnosticId(selectedTicketId),
            counts: {
                visibleTickets: filteredTicketsToDisplay.length,
                ticketsInContext: ticketsList.length,
                fetchedTickets: tickets?.length || 0
            },
            metadata: {
                listStatus: listStatus || "all"
            }
        });
    }, [filteredTicketsToDisplay.length, ticketsList.length, tickets?.length, listStatus, selectedTicketId]);

    return (
        <Paper className={classes.ticketsListWrapper} style={style}>
            <Paper
                square
                elevation={0}
                className={classes.ticketsList}
                onScroll={handleScroll}
            >
                <List style={{ paddingTop: 0 }}>
                    {filteredTicketsToDisplay.length === 0 && !loading ? (
                        <div className={classes.noTicketsDiv}>
                            <span className={classes.noTicketsTitle}>
                                {i18n.t("ticketsList.noTicketsTitle")}
                            </span>
                            <p className={classes.noTicketsText}>
                                {i18n.t("ticketsList.noTicketsMessage")}
                            </p>
                        </div>
                    ) : (
                        filteredTicketsToDisplay.map((ticket) => (
                            <TicketListItem
                                key={ticket.id}
                                ticket={ticket}
                                onTicketSelect={handleTicketSelect}
                                onOpenIgnoreModal={handleOpenIgnoreModal}
                            />
                        ))
                    )}
                    {loading && <TicketsListSkeleton />}
                </List>
            </Paper>
        </Paper>
    );
};

export default TicketsList;
